import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { useCallback, useMemo } from "react";
import { ModalLoading } from "../../../../../../common/components/modal_loading";
import {
  axisClasses,
  barClasses,
  barLabelClasses,
  BarPlot,
  ChartsContainer,
  ChartsGrid,
  ChartsXAxis,
  ChartsYAxis
} from "@mui/x-charts";
import { BarProps } from "@mui/x-charts/BarChart/AnimatedBarElement";
import { EntregaObraGrafico } from "../../../../domain/models/entrega_obra_cliente_grafico";

/**
 * CustomBar
 */

interface CustomBarProps extends BarProps {
  colors: string[];
}

const CustomBar = ({
  id,
  dataIndex,
  colors,
  ownerState,
  skipAnimation,
  xOrigin,
  yOrigin,
  ...props
}: CustomBarProps) => {
  return (
    <rect {...props} color={colors[dataIndex]} fill={colors[dataIndex]} />
  );
}

/**
 * Modal
 */

type Props = {
  open: boolean;
  loading: boolean;
  entregaObraGrafico: EntregaObraGrafico | null;
  onClose: () => void;
};

export const EntregaObraChecklistServicoGraficoModal = ({
  open,
  loading,
  entregaObraGrafico,
  onClose,
}: Props) => {


  const dataset = useMemo(() => {
    if (!entregaObraGrafico) return [];
    return [
      {
        quantidade: entregaObraGrafico.totalA ?? 0,
        period: "Total de Aprovações (A)",
      },
      {
        quantidade: entregaObraGrafico.totalR ?? 0,
        period: "Total de Retrabalhos (R)"
      },
      {
        quantidade: entregaObraGrafico.totalX ?? 0,
        period: "Total de Reprovações (X)"
      },
      {
        quantidade: entregaObraGrafico.totalRA ?? 0,
        period: "Total de Reaprovações (RA)"
      },
      {
        quantidade: entregaObraGrafico.totalNA ?? 0,
        period: "Não se aplica (NA)"
      }
    ];
  }, [entregaObraGrafico]);

  const maxValue = useMemo(() => {
    if (!entregaObraGrafico) return 0;

    const values = [
      entregaObraGrafico.totalA ?? 0,
      entregaObraGrafico.totalR ?? 0,
      entregaObraGrafico.totalX ?? 0,
      entregaObraGrafico.totalRA ?? 0,
      entregaObraGrafico.totalNA ?? 0
    ];

    const max = Math.max(...values);
    return max + (max * 0.1);
  }, [entregaObraGrafico]);

  const colors = useMemo(() => {
    return ['#26c281', '#ffff00', '#fb3113', '#ff8000', '#8ea3df'];
  }, []);

  const xAxis = useMemo((): any => {
    return [
      {
        scaleType: "band",
        dataKey: 'period',
      },
    ];
  }, []);

  const series = useMemo((): any => {
    return [
      { id: 1, type: 'bar', dataKey: 'quantidade', barLabel: 'value' },

    ];
  }, []);

  const chartSetting = useMemo(() => ({
    sx: {
      [`& .${axisClasses.directionY} .${axisClasses.label}`]: {
        transform: 'translateX(-10px)',
      },
      [`& .${axisClasses.directionX} .${axisClasses.label}`]: {
        transform: 'translateY(-10px)',
      },
    },
  }), []);

  const handleClose = useCallback(() => {
    if (!loading) onClose();
  }, [loading, onClose]);

  return (
    <Dialog
      open={open}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "70rem",
          },
        },
      }}>
      <DialogTitle>
        Gráfico Resumo
      </DialogTitle>

      <DialogContent>
        <ModalLoading loading={loading} message="Carregando...">
          <div className="w-full h-[30rem]">

            <ChartsContainer
              dataset={dataset}
              xAxis={xAxis}
              yAxis={[{ min: 0, max: maxValue }]}
              series={series}
              {...chartSetting}
              sx={{
                [`& .${barClasses.root} .${barClasses.seriesLabels} .${barLabelClasses.root}`]: {
                  fill: 'white',
                  fontSize: '18px',
                  paintOrder: 'stroke',
                  stroke: '#000000',
                  strokeWidth: '2px',
                  fontWeight: 800,
                },
              }}>

              <ChartsGrid horizontal />

              <BarPlot
                slots={{ bar: CustomBar as any }}
                slotProps={{ bar: { colors } as any }}
              />

              <ChartsXAxis />
              <ChartsYAxis />

            </ChartsContainer>

          </div>
        </ModalLoading>
      </DialogContent>

      <DialogActions>
        <div className="w-full flex justify-end p-4">
          <div
            className="p-2 hover:bg-hover-light-gray cursor-pointer rounded font-semibold"
            onClick={handleClose}>
            Fechar
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
};